import React from 'react';
import PropTypes from 'prop-types';
import { countries } from 'platform/forms/address';

// Render only populated address lines to avoid empty spacing in the block.
const renderAddressLine = content =>
  content ? (
    <>
      {content}
      <br />
    </>
  ) : null;

const addressPropType = PropTypes.shape({
  street: PropTypes.string,
  street2: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  postalCode: PropTypes.string,
  country: PropTypes.string,
});

/**
 * The no-suggestion branch of USPS validation.
 *
 * If the top suggestion was an exact/high-confidence match, show a success
 * alert. Otherwise (no usable suggestions or the API was unavailable) show a
 * warning and let the user continue or go back — validation never blocks.
 *
 * `isUndeliverable` is the case where validation didn't run at all: the
 * address is the one on file in VA Profile and mail sent to it was returned.
 */
export default function AddressConfirmation({
  subHeader,
  headerLevel,
  userAddress,
  isConfirmedMatch,
  isServiceError,
  isUndeliverable,
}) {
  const Title = headerLevel;
  const AlertHeadline = headerLevel === 'h1' ? 'h2' : 'h3';
  const cityStatePostal = [
    userAddress?.city,
    userAddress?.city && (userAddress?.state || userAddress?.postalCode)
      ? ', '
      : ' ',
    userAddress?.state,
    userAddress?.state && userAddress?.postalCode ? ' ' : '',
    userAddress?.postalCode,
  ]
    .join('')
    .trim();

  const getCountry = countryCode =>
    countries.find(c => c.value === countryCode)?.label || countryCode;

  return (
    <>
      <Title>{subHeader}</Title>
      {isUndeliverable && (
        <va-alert
          status="warning"
          visible
          data-testid="undeliverable-address-alert"
        >
          <AlertHeadline slot="headline">
            We couldn’t deliver mail to this address recently
          </AlertHeadline>
          <p className="vads-u-margin-y--0">
            Review your address to make sure it’s correct. If your address
            changed, select Back to update it.
          </p>
        </va-alert>
      )}
      {!isUndeliverable && isConfirmedMatch && (
        <va-alert
          close-btn-aria-label="Close notification"
          status="success"
          visible
        >
          <AlertHeadline slot="headline">
            We confirmed your address
          </AlertHeadline>
          <p className="vads-u-margin-y--0">
            The address you entered matches U.S. Postal Service records.
          </p>
        </va-alert>
      )}
      {!isUndeliverable && !isConfirmedMatch && (
        <va-alert
          close-btn-aria-label="Close notification"
          status="warning"
          visible
        >
          {isServiceError ? (
            <>
              <AlertHeadline slot="headline">
                We can’t check your address right now
              </AlertHeadline>
              <p className="vads-u-margin-y--0">
                We’re sorry. Our address confirmation tool is temporarily
                unavailable. This doesn’t mean there’s a problem with the
                address you entered — you can continue with it.
              </p>
            </>
          ) : (
            <>
              <AlertHeadline slot="headline">
                Check the address you entered
              </AlertHeadline>
              <p className="vads-u-margin-y--0">
                We can’t confirm the address you entered with the U.S. Postal
                Service. Check the address before continuing.
              </p>
            </>
          )}
        </va-alert>
      )}
      <p style={{ marginTop: '1em' }}>
        {isConfirmedMatch || isUndeliverable
          ? 'We’ll use this address:'
          : 'You entered:'}
      </p>
      <div className="blue-bar-block">
        <p>
          {renderAddressLine(userAddress?.street)}
          {renderAddressLine(userAddress?.street2)}
          {cityStatePostal && renderAddressLine(cityStatePostal)}
          {userAddress?.country !== 'USA' &&
            renderAddressLine(getCountry(userAddress?.country))}
        </p>
      </div>
      <p>
        If the address is correct, you can continue. If you need to edit the
        address, you can go back.
      </p>
      {!isConfirmedMatch && !isServiceError && !isUndeliverable && (
        <va-additional-info trigger="Why we can’t confirm the address you entered">
          <p>
            The address you entered may not be in the U.S. Postal Service’s
            system. Or, you may have entered an error or other incorrect
            information.
          </p>
        </va-additional-info>
      )}
    </>
  );
}

AddressConfirmation.propTypes = {
  subHeader: PropTypes.string.isRequired,
  userAddress: addressPropType.isRequired,
  headerLevel: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4']),
  isConfirmedMatch: PropTypes.bool,
  isServiceError: PropTypes.bool,
  isUndeliverable: PropTypes.bool,
};

AddressConfirmation.defaultProps = {
  headerLevel: 'h3',
  isConfirmedMatch: false,
  isServiceError: false,
  isUndeliverable: false,
};
